import { Link, useLocation, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import type {
  SponsorConfig,
  ContentSection,
  GuestRosterSection,
  BenefitListSection,
  EconomicsSection,
  SocialsSection,
  ValueProofSection,
  PackageBlockSection,
  MediaSection,
  ImageGridSection,
  QuoteSection,
} from '../shared/tokens/sponsor-config.types'
import NotFound from './NotFound.tsx'
import HeroC from '../components/HeroC.tsx'
import PlatformIcon, { inferPlatform } from '../components/PlatformIcon.tsx'
import { useAuth } from '../contexts/AuthContext.tsx'
import NDAGate from '../components/gates/NDAGate.tsx'
import { MASTER_HERO_SOCIAL_LINKS } from '../lib/masterAudience.ts'

/**
 * Sponsor pitch page.
 *
 * Renders per-sponsor page from config JSON.
 * Supports section types: text, guest-roster, benefit-list, economics, package-block.
 *
 * Per Directive #17 Gate 4 (Sarala publish-gate): only /ratified/ files render here.
 * Files in /draft/ render at /preview/:slug.
 * Per SS-SD-EXTERNAL-1 (Directive #18): no infrastructure disclosure.
 *
 * Accepts optional defaultSlug for explicit sponsor containers that do not carry
 * URL parameters. The public root must not render sponsor pitch content.
 */

interface SponsorPageProps {
  defaultSlug?: string
  publicCopy?: boolean
  publicProspect?: boolean
}

const RESERVED_TOP_LEVEL_PATHS = new Set([
  'account',
  'admin',
  'home',
  'privacy',
  'request-access',
  'signin',
  'status',
  'terms',
])

function renderGuestRoster(section: GuestRosterSection, i: number) {
  return (
    <section key={i} className="section-guest-roster">
      <h2>{section.heading}</h2>
      {section.body && <p className="section-intro">{section.body}</p>}
      <div className="guest-grid">
        {section.guests.map((guest, j) => (
          <div key={j} className="guest-card">
            <span className="guest-name">{guest.name}</span>
            <span className="guest-reach">{guest.reach}</span>
            <span className="guest-category">{guest.category}</span>
          </div>
        ))}
      </div>
    </section>
  )
}

function renderBenefitList(section: BenefitListSection, i: number) {
  return (
    <section key={i} className="section-benefit-list">
      <h2>{section.heading}</h2>
      {section.body && <p className="section-intro">{section.body}</p>}
      <dl className="benefit-grid">
        {section.benefits.map((benefit, j) => (
          <div key={j} className="benefit-item">
            <dt>{benefit.name}</dt>
            <dd>{benefit.description}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

function renderEconomics(section: EconomicsSection, i: number) {
  return (
    <section key={i} className="section-economics">
      <h2>{section.heading}</h2>
      {section.body && <p className="section-intro">{section.body}</p>}
      <div className="metrics-grid">
        {section.metrics.map((metric, j) => (
          <div key={j} className="metric-item">
            <span className="metric-label">{metric.label}</span>
            <span className="metric-value">{metric.value}</span>
          </div>
        ))}
      </div>
      {section.comparisons && section.comparisons.length > 0 && (
        <table className="comparison-table">
          <thead>
            <tr>
              <th>Alternative</th>
              <th>Cost</th>
              <th>Endorsement</th>
              <th>Production</th>
            </tr>
          </thead>
          <tbody>
            {section.comparisons.map((row, j) => (
              <tr key={j} className={row.alternative.startsWith('Yan-Vada') ? 'highlight-row' : ''}>
                <td>{row.alternative}</td>
                <td>{row.cost}</td>
                <td>{row.endorsement}</td>
                <td>{row.production}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

function renderSocials(section: SocialsSection, i: number) {
  return (
    <section key={i} className="section-socials">
      <div className="socials-header">
        <h2>{section.heading}</h2>
        {section.body && <p>{section.body}</p>}
        {!!section.links?.length && (
          <div className="socials-report-links" aria-label="Supporting sponsor reports">
            {section.links.map((link, j) => (
              <a key={j} href={link.href} target="_blank" rel="noopener noreferrer">{link.label}</a>
            ))}
          </div>
        )}
      </div>
      <div className="socials-grid">
        {section.platforms.map((platform, j) => {
          const platformKey = platform.platform ?? inferPlatform(platform.label, platform.href)
          const content = (
            <>
              <span className="socials-icon-wrap"><PlatformIcon platform={platformKey} /></span>
              <span className="socials-copy">
                <span className="socials-platform">{platform.label}</span>
                <span className="socials-metric">{platform.metric_value ?? 'Audit pending'}</span>
                {platform.metric_label && <span className="socials-label">{platform.metric_label}</span>}
              </span>
            </>
          )
          return platform.href ? (
            <a key={j} className="socials-card" data-platform={platformKey} href={platform.href} target="_blank" rel="noopener noreferrer">
              {content}
            </a>
          ) : (
            <div key={j} className="socials-card" data-platform={platformKey}>
              {content}
            </div>
          )
        })}
      </div>
    </section>
  )
}

function valueProofRole(card: ValueProofSection['cards'][number], index: number) {
  if (card.featured) return 'featured'
  const href = card.href ?? ''
  if (href.includes('replacement-check')) return 'replacement'
  if (href.includes('expanded-engagement')) return 'engagement'
  if (href.includes('host-base')) return 'host-base'
  if (href.includes('guest-platform-lift')) return 'guest-lift'
  return `proof-${index + 1}`
}

function renderValueProof(section: ValueProofSection, i: number) {
  const currentView = section.current_view
  return (
    <section key={i} id={section.id} className="section-value-proof">
      <div className="value-proof-story">
        {section.eyebrow && <p className="value-proof-eyebrow">{section.eyebrow}</p>}
        <h2>{section.heading}</h2>
        {section.body && <p>{section.body}</p>}
        {section.action?.href && (
          <a className="value-proof-action" href={section.action.href}>{section.action.label || 'Open value report'}</a>
        )}
      </div>
      <div className="value-proof-panel">
        <div className="value-proof-grid">
          {section.cards.map((card, j) => {
            const CardTag = card.href ? 'a' : 'article'
            return (
              <CardTag
                key={j}
                className="value-proof-card"
                data-proof-role={valueProofRole(card, j)}
                data-featured={card.featured ? 'true' : undefined}
                href={card.href}
              >
                <span className="value-proof-label">{card.label}</span>
                <span className="value-proof-stat">{card.stat}</span>
                {card.body && <span className="value-proof-copy">{card.body}</span>}
                {!!card.metrics?.length && (
                  <span className="value-proof-metrics">
                    {card.metrics.map((metric, metricIndex) => (
                      <span key={metricIndex}>
                        <strong>{metric.value}</strong>
                        <small>{metric.label}</small>
                      </span>
                    ))}
                  </span>
                )}
                {card.foot && <span className="value-proof-foot">{card.foot}</span>}
                {card.href && <span className="value-proof-link">Open evidence</span>}
              </CardTag>
            )
          })}
          {currentView && (
            <article className="value-proof-current" data-proof-role="current-view">
              <div className="value-proof-current-head">
                <span>
                  <span className="value-proof-label">{currentView.label}</span>
                  {currentView.body && <span className="value-proof-current-copy">{currentView.body}</span>}
                </span>
                <strong className="value-proof-current-stat">{currentView.stat}</strong>
              </div>
              <div className="value-proof-current-body">
                <div className="value-proof-current-list">
                  {currentView.items.map((item, itemIndex) => (
                    <span key={itemIndex} className="value-proof-current-item">
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                      {item.period && <small>{item.period}</small>}
                    </span>
                  ))}
                </div>
                {currentView.note && <p>{currentView.note}</p>}
              </div>
            </article>
          )}
        </div>
      </div>
    </section>
  )
}

/**
 * PackageBlock renderer — v3 asymmetric cards.
 *
 * Per PF-R2 §7: not a comparison table. Two cards side-by-side on desktop,
 * stacked on mobile. Asymmetric content lengths are HONEST (Title is a larger
 * package; it gets more copy).
 */
function renderPackageBlock(section: PackageBlockSection, i: number) {
  return (
    <section key={i} id="partnership-opportunity" className="section-package-block">
      {section.heading && <h2 className="package-block-heading">{section.heading}</h2>}
      {section.body && <p className="package-block-body">{section.body}</p>}
      <div className="package-grid">
        {section.packages.map((pkg, j) => (
          <article key={j} className="package-card" data-package={pkg.name.toLowerCase().replace(/\s+/g, '-')} data-featured={j === 0 ? 'true' : undefined}>
            <header className="package-header">
              <h3 className="package-name">{pkg.name}</h3>
              <p className="package-price">{pkg.price}</p>
              {pkg.price_note && <p className="package-price-note">{pkg.price_note}</p>}
            </header>
            {pkg.tagline && <p className="package-tagline">{pkg.tagline}</p>}
            <ul className="package-benefits">
              {pkg.benefits.map((benefit, k) => (
                <li key={k} className="package-benefit">{benefit}</li>
              ))}
            </ul>
            {pkg.footer && <p className="package-footer">{pkg.footer}</p>}
          </article>
        ))}
      </div>
    </section>
  )
}

function renderMediaSection(section: MediaSection, i: number) {
  if (!section.url) return null
  return (
    <section key={i} className="section-media">
      {section.heading && <h2>{section.heading}</h2>}
      {section.body && <p className="section-intro">{section.body}</p>}
      <figure>
        {section.media_type === 'video' ? (
          <video src={section.url} poster={section.poster} controls playsInline />
        ) : (
          <img src={section.url} alt={section.alt ?? ''} />
        )}
        {section.caption && <figcaption>{section.caption}</figcaption>}
      </figure>
    </section>
  )
}

function renderImageGridSection(section: ImageGridSection, i: number) {
  return (
    <section key={i} className="section-image-grid">
      {section.heading && <h2>{section.heading}</h2>}
      {section.body && <p className="section-intro">{section.body}</p>}
      <div className="image-grid-list">
        {section.images.map((image, j) => (
          <figure key={j}>
            <img src={image.url} alt={image.alt} />
            {image.caption && <figcaption>{image.caption}</figcaption>}
          </figure>
        ))}
      </div>
    </section>
  )
}

function renderQuoteSection(section: QuoteSection, i: number) {
  return (
    <section key={i} className="section-quote">
      <blockquote>{section.text}</blockquote>
      {section.attribution && <cite>{section.attribution}</cite>}
    </section>
  )
}

function renderSection(section: ContentSection, i: number) {
  switch (section.type) {
    case 'guest-roster':
      return renderGuestRoster(section, i)
    case 'benefit-list':
      return renderBenefitList(section, i)
    case 'economics':
      return renderEconomics(section, i)
    case 'socials':
      return renderSocials(section, i)
    case 'value-proof':
      return renderValueProof(section, i)
    case 'package-block':
      return renderPackageBlock(section, i)
    case 'media':
      return renderMediaSection(section, i)
    case 'image-grid':
      return renderImageGridSection(section, i)
    case 'quote':
      return renderQuoteSection(section, i)
    case 'text':
    default:
      return (
        <section key={i} className="section-text">
          {section.heading && <h2>{section.heading}</h2>}
          {section.body.split(/\n{2,}/).map((paragraph, j) => (
            <p key={j}>{paragraph}</p>
          ))}
        </section>
      )
  }
}

export default function SponsorPage({ defaultSlug, publicCopy = false, publicProspect = false }: SponsorPageProps = {}) {
  const params = useParams<{ slug: string }>()
  const location = useLocation()
  const slug = params.slug ?? defaultSlug
  const { isLoggedIn, acceptedNdaSponsors, loading } = useAuth()
  const [config, setConfig] = useState<SponsorConfig | null>(null)
  const [error, setError] = useState<string | null>(null)
  const ndaAcceptanceKey = acceptedNdaSponsors.join('|')
  const isMasterSlug = slug === 'master'
  const isPublicSurface = publicCopy || publicProspect

  useEffect(() => {
    let cancelled = false
    if (!slug) {
      setError('No sponsor specified')
      return
    }
    if (RESERVED_TOP_LEVEL_PATHS.has(slug)) {
      setError('not-found')
      return
    }
    if (!isPublicSurface && loading) return

    setConfig(null)
    setError(null)

    async function load() {
      try {
        const apiPath = publicProspect
          ? '/api/public/sponsor-pages/prospect'
          : publicCopy
            ? `/api/temporary/sponsors/${slug}`
            : `/api/sponsors/${slug}`
        const r = await fetch(apiPath, { credentials: 'include' })
        const contentType = r.headers.get('content-type') ?? ''
        if (!contentType.includes('json')) throw new Error(isMasterSlug ? 'auth_required' : 'not-found')
        if (!r.ok) {
          const data = await r.json().catch(() => ({})) as { error?: string }
          throw new Error(data.error === 'not_found' ? 'not-found' : (data.error ?? 'not-found'))
        }
        const data = publicProspect
          ? (await r.json() as { content?: SponsorConfig }).content
          : await r.json() as SponsorConfig
        if (!data) throw new Error('not-found')
        if (cancelled) return
        setConfig(data)
      } catch (err) {
        const message = err instanceof Error ? err.message : 'not-found'
        if (cancelled) return
        setError(message === 'not_found' ? 'not-found' : message)
      }
    }

    void load()
    return () => { cancelled = true }
  }, [slug, isLoggedIn, ndaAcceptanceKey, loading, isMasterSlug, publicCopy, publicProspect, isPublicSurface])

  if (error === 'not-found') return <NotFound />
  if (error === 'auth_required') {
    return (
      <main className="sponsor-page sponsor-page-state">
        <div className="sponsor-content sponsor-content-state">
          <section className="section-text">
            <h1>Sign in to view this proposal.</h1>
            <p>This page is intended for approved Yan-Vada partners and team members.</p>
            <Link to={`/signin?next=${encodeURIComponent(`${location.pathname}${location.search}`)}`} className="btn btn-primary">Sign in</Link>
          </section>
        </div>
      </main>
    )
  }
  if (error === 'sponsor_access_required') {
    return (
      <main className="sponsor-page sponsor-page-state">
        <div className="sponsor-content sponsor-content-state">
          <section className="section-text">
            <h1>Access is pending.</h1>
            <p>Your account is signed in, but this proposal has not been approved for your email yet.</p>
            <Link to="/request-access" className="btn btn-primary">Request access</Link>
          </section>
        </div>
      </main>
    )
  }
  if (error === 'nda_required') {
    return (
      <main className="sponsor-page sponsor-page-state">
        <div className="sponsor-content sponsor-content-state">
          <section className="section-text">
            <h1>NDA acceptance required.</h1>
            <p>Accept the confidentiality gate to unlock this proposal.</p>
          </section>
          {slug && <NDAGate sponsorSlug={slug} />}
        </div>
      </main>
    )
  }
  if (error) return <div className="error">{error}</div>
  if (!config) return <div className="loading">Loading…</div>

  const hasWhyFit = Array.isArray(config.content.why_fit) && config.content.why_fit.length > 0
  const hasCta = !!config.content.cta

  return (
    <main className="sponsor-page" data-surface="branding" data-sponsor={config.slug}>
      <HeroC
        headline={config.content.hero_headline}
        seasonLabel={config.content.hero_subhead}
        showCtaActions
        sponsorLogo={config.content.hero_sponsor_logo}
        sponsorName={config.content.hero_sponsor_name}
        heroLayout={config.content.hero_layout}
        mediaType={config.content.hero_video ? 'video' : 'image'}
        heroImageUrl={config.content.hero_poster || '/sunset-1.jpeg'}
        heroVideoUrl={config.content.hero_video || ''}
        heroVideoWebmUrl={config.content.hero_video_webm || ''}
        showScrollCue
        scrollTargetId="sponsor-content-start"
        socialLinks={isMasterSlug || publicProspect ? [...MASTER_HERO_SOCIAL_LINKS] : []}
      />

      <div id="sponsor-content-start" className="sponsor-content">
        {hasWhyFit && (
          <section className="why-fit">
            <h2>Why this fit</h2>
            <ul>
              {config.content.why_fit!.map((reason, i) => (
                <li key={i}>{reason}</li>
              ))}
            </ul>
          </section>
        )}

        {config.content.sections.map((section, i) => renderSection(section, i))}

        {hasCta && (
          <section className="cta">
            <a href={config.content.cta!.destination}>{config.content.cta!.text}</a>
          </section>
        )}
      </div>
    </main>
  )
}
